import { useId, useState, type InputHTMLAttributes, type ReactNode } from "react";
import { EyeIcon, EyeOffIcon } from "@/components/icons";
import { cn } from "@/lib/cn";

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  label: string;
  hint?: ReactNode;
  error?: ReactNode;
  /** Adds a show/hide toggle for secret values. */
  revealable?: boolean;
}

export function Field({
  label,
  hint,
  error,
  revealable = false,
  className,
  type = "text",
  ...props
}: FieldProps) {
  const id = useId();
  const [revealed, setRevealed] = useState(false);
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  const inputType = revealable ? (revealed ? "text" : "password") : type;

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="type-label text-ink">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={inputType}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(
            "w-full control-lg rounded-element bg-card text-ink type-body",
            "border px-3 placeholder:text-ink-disabled",
            "transition-[border-color,box-shadow] motion-fast",
            error
              ? "border-error focus:border-error"
              : "border-border hover:border-border-emphasized focus:border-accent",
            revealable && "pe-11",
            className,
          )}
          {...props}
        />
        {revealable ? (
          <button
            type="button"
            onClick={() => setRevealed((value) => !value)}
            aria-label={revealed ? "Hide access key" : "Show access key"}
            className={cn(
              "absolute end-1 top-1/2 -translate-y-1/2 rounded-inner p-2",
              "text-ink-secondary hover:text-ink hover:bg-hover motion-fast",
            )}
          >
            {revealed ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
          </button>
        ) : null}
      </div>
      {error ? (
        <p id={`${id}-error`} role="alert" className="type-support text-error">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="type-support text-ink-secondary">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
